import { useEffect, useMemo, useState } from 'react';
import { useAuthentication, useNotification, useTranslation } from '@topcon/topcon-utils';
import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeModal,
  TeSearch,
  TeSearchSpotlight,
  TeSegment,
  TeSegmentText,
  TeSpotlightSlot,
  TeText,
} from '@topcon/tuicl-react';
import { TeChangeEvent } from '@topcon/tuicl';

import {
  MigrationCandidateBusinessPartnerBody,
  MigrationCandidateMatchTarget,
} from '../../api/generated/pm';
import { BusinessPartnerWithCurrentIdentityInfoBody } from '../../api/generated/pap';
import { formatAddress } from '../../utils/formatAddress';
import CompanyIcon from '../../components/Company/CompanyIcon';
import OrgCard from '../../components/OrgCard';
import IncomingOrganizationCard from '../../components/Migration/IncomingOrganizationCard';
import NoMatchesEmptyState from '../../components/Migration/NoMatchesEmptyState';
import PotentialMatchRow from '../../components/Migration/PotentialMatchRow';
import { useAcceptMigrationCandidateBusinessPartner } from '../../hooks/queries/migration.mutations';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import { useIdentityCurrentBusinessPartners } from './reviewOrganization.queries';
import { formatOrganizationNumber } from '../../utils/organizationNumberFormatter';

interface Props {
  organization: MigrationCandidateBusinessPartnerBody | null;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
  onAddAsNewOrganization: (organization: MigrationCandidateBusinessPartnerBody) => void;
}

// ── Main component ───────────────────────────────────────────────────────────

function ReviewOrganizationModal({ organization, open, onClose, onAddAsNewOrganization }: Props) {
  const { t } = useTranslation('companies', { keyPrefix: 'pendingMerges' });
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { identityId } = useAuthentication();
  const address = formatAddress(organization?.address);

  const [activeTab, setActiveTab] = useState<'matches' | 'search'>('matches');
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchTabActivated, setSearchTabActivated] = useState(false);
  const [showSpotlight, setShowSpotlight] = useState(false);

  const { mutateAsync: acceptCandidate, isPending } = useAcceptMigrationCandidateBusinessPartner();
  const { data: bpListData } = useIdentityCurrentBusinessPartners(searchTabActivated);

  const topTargets = organization?.matchDetails?.top_targets ?? [];

  const eligibleBPs = useMemo(() => {
    const items = bpListData?.items ?? [];
    return items.filter(
      (bp: BusinessPartnerWithCurrentIdentityInfoBody) =>
        (bp.user?.identityId === identityId || bp.isOwner === true) &&
        bp.type.startsWith('end_customer')
    );
  }, [bpListData, identityId]);

  const filteredBPs = useMemo(() => {
    const lower = searchText.trim().toLowerCase();
    if (!lower) return eligibleBPs;
    return eligibleBPs.filter((bp: BusinessPartnerWithCurrentIdentityInfoBody) =>
      bp.name.toLowerCase().includes(lower)
    );
  }, [eligibleBPs, searchText]);

  const selectedBP = useMemo(
    () =>
      eligibleBPs.find(
        (bp: BusinessPartnerWithCurrentIdentityInfoBody) => bp.extId === selectedExternalId
      ),
    [eligibleBPs, selectedExternalId]
  );

  // Reset local state when the candidate under review changes.
  useEffect(() => {
    setActiveTab('matches');
    setSelectedExternalId(null);
    setSearchText('');
    setSearchTabActivated(false);
    setShowSpotlight(false);
  }, [organization?.id]);

  const handleSwitchToSearch = () => {
    setActiveTab('search');
    setSelectedExternalId(null);
    setSearchText('');
    setShowSpotlight(false);
    setSearchTabActivated(true);
  };

  const handleSwitchToMatches = () => {
    setActiveTab('matches');
    setSelectedExternalId(null);
  };

  const handleMerge = async () => {
    if (!organization || !selectedExternalId || isPending) return;
    try {
      await acceptCandidate({ id: organization.id, externalId: selectedExternalId });
      onClose(true);
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('reviewModal.mergeFailed'),
          t('reviewModal.mergeFailedDescription', { name: organization.name }),
          'error'
        );
      }
    }
  };

  const handleSearchChange = (e: TeChangeEvent) => {
    setSearchText((e.target as HTMLInputElement).value);
    setSelectedExternalId(null);
    setShowSpotlight(true);
  };

  const handleSelectSearchResult = (bp: BusinessPartnerWithCurrentIdentityInfoBody) => {
    setSelectedExternalId(bp.extId);
    setSearchText(bp.name);
    setShowSpotlight(false);
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      closeable
      modalTitle={t('reviewModal.title')}
      teclose={() => !isPending && onClose(false)}
      contentPadding={0}
      width="l"
    >
      <TeContainer
        backgroundColor="background.primary"
        padding="s"
        column
        grow
        fill
        gap="m"
        overflow="auto"
      >
        {/* Incoming candidate */}
        <IncomingOrganizationCard
          label={t('reviewModal.incomingOrganizationFrom')}
          name={organization?.name ?? ''}
          sourceSystem={organization?.sourceSystem}
          address={address}
        />

        {/* Tab control + content */}
        <TeContainerSlim column gap="s" fill>
          <TeSegment grow size="s" variant="secondary">
            <TeSegmentText
              text={t('reviewModal.potentialMatches')}
              active={activeTab === 'matches'}
              teclick={handleSwitchToMatches}
            />
            <TeSegmentText
              text={t('reviewModal.searchForOrganization')}
              active={activeTab === 'search'}
              teclick={handleSwitchToSearch}
            />
          </TeSegment>

          {activeTab === 'matches' && (
            <TeContainerSlim column gap="s" fill>
              <TeDivider
                text={
                  topTargets.length === 0
                    ? t('reviewModal.noMatchesHeading')
                    : t('reviewModal.possibleMatchesHeading')
                }
                icon="info"
                textPlacement="center"
                dividerColor="border.primary"
                textColor="text.primary"
              />
              {topTargets.length === 0 ? (
                <NoMatchesEmptyState
                  title={t('reviewModal.noMatchesFound')}
                  description={t('reviewModal.noMatchesDescription')}
                />
              ) : (
                topTargets.map((target: MigrationCandidateMatchTarget) => (
                  <PotentialMatchRow
                    key={target.external_id}
                    target={target}
                    selected={selectedExternalId === target.external_id}
                    onSelect={() =>
                      setSelectedExternalId((current) =>
                        current === target.external_id ? null : target.external_id
                      )
                    }
                    labels={{
                      score: t('reviewModal.matchScore', {
                        score: Math.round(target.score),
                      }),
                      address: t('reviewModal.addressMatched'),
                      businessName: t('reviewModal.businessNameMatched'),
                      domain: t('reviewModal.domainMatched'),
                    }}
                  />
                ))
              )}
            </TeContainerSlim>
          )}

          {activeTab === 'search' && (
            <TeContainerSlim column gap="xs" fill>
              <TeText size={14} weight={500}>
                {t('reviewModal.companyNameLabel')}
              </TeText>
              <TeSearch
                size="s"
                grow
                icon="search"
                clearable
                placeholder={t('reviewModal.searchPlaceholder')}
                value={searchText}
                techange={handleSearchChange}
                teclear={() => {
                  setSearchText('');
                  setSelectedExternalId(null);
                  setShowSpotlight(false);
                }}
              >
                {showSpotlight && searchText.trim() !== '' && (
                  <TeSearchSpotlight
                    fixedPosition
                    style={{ maxHeight: '300px' }}
                    teclose={() => setShowSpotlight(false)}
                  >
                    {filteredBPs.length === 0 ? (
                      <TeSpotlightSlot key="no-results">
                        <TeContainerSlim padding="m" fill grow justifyContent="center">
                          <TeText color="text.secondary" alignment="center">
                            {t('reviewModal.searchNoResults')}
                          </TeText>
                        </TeContainerSlim>
                      </TeSpotlightSlot>
                    ) : (
                      filteredBPs.map((bp: BusinessPartnerWithCurrentIdentityInfoBody) => (
                        <TeSpotlightSlot
                          key={bp.id}
                          teclick={() => handleSelectSearchResult(bp)}
                          data-testid={`org-row-${bp.extId}`}
                        >
                          <TeContainerSlim gap="xs" alignItems="center" padding="xs" fill grow>
                            <CompanyIcon size="s" iconURL={bp.iconURL} />
                            <TeContainerSlim column gap="xs3" alignItems="flex-start" grow>
                              <TeText size={14} weight={500}>
                                {bp.name}
                              </TeText>
                              <TeText size={12} color="text.secondary">
                                {formatOrganizationNumber(bp.extId) || t('reviewModal.noAddress')}
                              </TeText>
                            </TeContainerSlim>
                          </TeContainerSlim>
                        </TeSpotlightSlot>
                      ))
                    )}
                  </TeSearchSpotlight>
                )}
              </TeSearch>

              {selectedBP ? (
                <OrgCard
                  name={selectedBP.name}
                  externalId={selectedBP.extId}
                  iconURL={selectedBP.iconURL}
                  selected
                />
              ) : (
                <TeContainer
                  backgroundColor="background.secondary"
                  borderColor="border.secondary"
                  borderRadius="l"
                  column
                  alignItems="center"
                  justifyContent="center"
                  padding="xl"
                  height="auto"
                  fill
                  data-testid="search-empty-state"
                >
                  <TeText size={14} color="text.secondary" italic alignment="center">
                    {t('reviewModal.searchEmptyState')}
                  </TeText>
                </TeContainer>
              )}
            </TeContainerSlim>
          )}
        </TeContainerSlim>
      </TeContainer>

      <TeButton
        slot="tertiary"
        text={t('reviewModal.cancel')}
        variant="neutral"
        size="s"
        disabled={isPending}
        teclick={() => !isPending && onClose(false)}
      />
      <TeButton
        slot="secondary"
        text={t('reviewModal.addAsNewOrganization')}
        variant="neutral"
        size="s"
        disabled={selectedExternalId !== null}
        teclick={() => organization && onAddAsNewOrganization(organization)}
      />
      <TeButton
        slot="primary"
        text={t('reviewModal.merge')}
        variant="primary"
        size="s"
        disabled={!selectedExternalId || isPending}
        loading={isPending}
        teclick={handleMerge}
      />
    </TeModal>
  );
}

export default ReviewOrganizationModal;
